import { z } from 'zod'

/**
 * Single entry point for build-time configuration.
 *
 * Only `VITE_*` values reach the browser bundle, so nothing secret may live
 * here (see AGENTS.md). The Supabase anon key is publishable by design; the
 * service-role key must never appear in this file or in `.env`.
 */
const booleanFlag = z
  .union([z.literal('true'), z.literal('false')])
  .default('false')
  .transform((value) => value === 'true')

const envSchema = z.object({
  VITE_APP_NAME: z.string().min(1).default('AutoBooking'),
  VITE_APP_ENV: z.enum(['development', 'staging', 'production']).default('development'),
  VITE_DEFAULT_TENANT_SLUG: z.string().min(1).default('demo'),

  // Optional until stage 3 (Supabase backend) wires a real project.
  VITE_SUPABASE_URL: z.url().optional(),
  VITE_SUPABASE_ANON_KEY: z.string().min(1).optional(),

  /** Publishable by design: the private half never leaves the server. */
  VITE_VAPID_PUBLIC_KEY: z.string().min(1).optional(),

  VITE_ENABLE_PWA: booleanFlag,
  VITE_ENABLE_AI_ASSISTANT: booleanFlag,
})

export type Env = z.infer<typeof envSchema>

function parseEnv(source: ImportMetaEnv): Env {
  const result = envSchema.safeParse(source)

  if (!result.success) {
    const issues = result.error.issues
      .map((issue) => `  - ${issue.path.join('.') || '(root)'}: ${issue.message}`)
      .join('\n')

    throw new Error(`Invalid environment configuration:\n${issues}\n\nSee .env.example.`)
  }

  return result.data
}

export const env: Env = parseEnv(import.meta.env)

/** True only when both Supabase values are present. Checked before any client is built. */
export const isSupabaseConfigured: boolean =
  env.VITE_SUPABASE_URL !== undefined && env.VITE_SUPABASE_ANON_KEY !== undefined

/** Push can only be offered when the public key is configured. */
export const isPushConfigured: boolean = env.VITE_VAPID_PUBLIC_KEY !== undefined

export const featureFlags = {
  pwa: env.VITE_ENABLE_PWA,
  aiAssistant: env.VITE_ENABLE_AI_ASSISTANT,
} as const
